import { useEffect, useMemo, useState } from 'react'
import ReactECharts from 'echarts-for-react'
import { getPatCoverage } from '../../../../services/analytics/analytics.service'
import Loading from '../../../Loading/Loading'
import DataAsOf from './DataAsOf'

const C_HAS = '#2b5784'
const C_NONE = '#cfd6e0'

const ylNum = (y) => {
  const n = parseInt(String(y).replace(/[^\d]/g, ''), 10)
  return Number.isNaN(n) ? 999 : n
}

const buildOption = (rows) => {
  const sorted = [...rows].sort((a, b) => ylNum(a.year_level) - ylNum(b.year_level))
  const levels = sorted.map((r) => 'Year ' + r.year_level)
  const withData = sorted.map((r) => Number(r.with_data || 0))
  const without = sorted.map((r) => Number(r.without_data || 0))
  const enrolled = sorted.map((r) => Number(r.enrolled || 0))

  return {
    backgroundColor: 'transparent',
    grid: { left: 62, right: 54, top: 12, bottom: 36 },
    legend: { bottom: 0, textStyle: { fontSize: 11, color: '#666' }, itemHeight: 10, itemWidth: 14 },
    xAxis: {
      type: 'value',
      axisLabel: { fontSize: 10, color: '#aaa' },
      splitLine: { lineStyle: { color: '#f0f0f0', type: 'dashed' } }
    },
    yAxis: {
      type: 'category',
      data: levels,
      axisLabel: { fontSize: 11, color: '#555' },
      axisTick: { show: false },
      axisLine: { lineStyle: { color: '#e0e0e0' } }
    },
    series: [
      {
        name: 'Has PAT data',
        type: 'bar',
        stack: 'cov',
        itemStyle: { color: C_HAS, borderRadius: [3, 0, 0, 3] },
        label: {
          show: true,
          position: 'inside',
          fontSize: 10,
          color: '#fff',
          formatter: (p) => {
            const total = enrolled[p.dataIndex] || 1
            const pct = Math.round((p.value / total) * 100)
            return p.value ? pct + '%' : ''
          }
        },
        data: withData
      },
      {
        name: 'No matched sitting',
        type: 'bar',
        stack: 'cov',
        itemStyle: { color: C_NONE, borderRadius: [0, 3, 3, 0] },
        label: {
          show: true,
          position: 'right',
          fontSize: 10,
          color: '#c0392b',
          formatter: (p) => (p.value ? p.value : '')
        },
        data: without
      }
    ],
    tooltip: {
      trigger: 'axis',
      axisPointer: { type: 'shadow' },
      formatter: (params) => {
        const i = params[0].dataIndex
        return `<b>${levels[i]}</b><br/>${withData[i]} with data · ${without[i]} missing<br/>${enrolled[i]} enrolled`
      }
    }
  }
}

const PatCoverage = () => {
  const [data, setData] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  useEffect(() => {
    getPatCoverage()
      .then((res) => setData(res?.data || []))
      .catch((err) => setError(err?.message || 'Error loading PAT coverage'))
      .finally(() => setLoading(false))
  }, [])

  const option = useMemo(() => buildOption(data), [data])
  const totals = useMemo(() => {
    const withData = data.reduce((a, r) => a + Number(r.with_data || 0), 0)
    const enrolled = data.reduce((a, r) => a + Number(r.enrolled || 0), 0)
    return { withData, enrolled, pct: enrolled ? Math.round((withData / enrolled) * 100) : 0 }
  }, [data])

  return (
    <div style={{ padding: 24 }}>
      <div
        style={{
          display: 'flex',
          alignItems: 'baseline',
          justifyContent: 'space-between',
          gap: 12,
          marginBottom: 16,
          flexWrap: 'wrap'
        }}
      >
        <div>
          <h2 style={{ margin: 0, fontSize: 18, fontWeight: 600, color: C_HAS }}>
            PAT data coverage by year level
          </h2>
          <DataAsOf />
        </div>
        {!!data.length && (
          <span style={{ fontSize: 13, color: '#666' }}>
            <b style={{ color: C_HAS }}>{totals.pct}%</b> overall · {totals.withData}/{totals.enrolled}{' '}
            students
          </span>
        )}
      </div>

      {loading ? (
        <Loading />
      ) : error ? (
        <div style={{ padding: 24, color: '#c0392b' }}>
          <strong>Error:</strong> {error}
        </div>
      ) : !data.length ? (
        <p style={{ color: '#888', padding: 24 }}>
          No roster/PAT overlap yet — needs the SQL Server roster + a PAT sync.
        </p>
      ) : (
        <ReactECharts
          option={option}
          style={{ height: Math.max(300, data.length * 46), width: '100%' }}
          opts={{ renderer: 'canvas' }}
          notMerge
        />
      )}
    </div>
  )
}

export default PatCoverage
